import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Loading, Modal, inputCls, labelCls, btnPrimary, fmtDate } from "@/components/kit";
import { CalendarBlank, Plus, Trophy, Barbell, Trash, MapPin, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmtTime = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const dayKey = (iso) => new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

export default function Schedule() {
  const { user } = useAuth();
  const [events, setEvents] = useState(null);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const canEdit = ["coach", "athletic_director"].includes(user?.role);
  const [form, setForm] = useState({ team_id: "", type: "practice", title: "", opponent: "", location: "", date: "", time: "16:00" });

  const load = async () => {
    const [e, t] = await Promise.all([api.get("/events"), canEdit ? api.get("/teams") : Promise.resolve({ data: [] })]);
    setEvents(e.data);
    setTeams(t.data);
    if (t.data?.[0]) setForm((f) => ({ ...f, team_id: f.team_id || t.data[0].id }));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.team_id || !form.title || !form.date) { toast.error("Fill team, title and date"); return; }
    setSaving(true);
    try {
      const start = new Date(`${form.date}T${form.time}:00`).toISOString();
      await api.post("/events", { team_id: form.team_id, type: form.type, title: form.title, opponent: form.opponent || null, location: form.location || null, start });
      toast.success("Event added");
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); } finally { setSaving(false); }
  };

  const del = async (id) => { await api.delete(`/events/${id}`); toast.success("Removed"); load(); };

  if (!events) return <Loading />;

  const grouped = {};
  events.forEach((e) => { const k = dayKey(e.start); (grouped[k] = grouped[k] || []).push(e); });

  return (
    <div className="px-4 md:px-8 py-8 md:py-12 max-w-4xl mx-auto">
      <PageHeader eyebrow="Games & Practices" title="Schedule" icon={CalendarBlank}>
        {canEdit && <button onClick={() => setOpen(true)} className={btnPrimary} data-testid="add-event-button"><Plus size={18} weight="bold" /> Add Event</button>}
      </PageHeader>

      {events.length === 0 ? (
        <Card className="p-12 text-center"><CalendarBlank size={40} weight="duotone" color="#737373" className="mx-auto mb-4" /><p className="text-[#A3A3A3]">No events scheduled.</p></Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([day, items]) => (
            <div key={day}>
              <div className="text-xs uppercase tracking-[0.2em] text-[#FF3B30] font-bold mb-3">{day}</div>
              <div className="space-y-3">
                {items.map((e) => {
                  const isGame = e.type === "game";
                  return (
                    <Card key={e.id} className="p-4 md:p-5 flex items-center gap-4" data-testid={`schedule-event-${e.id}`}>
                      <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${isGame ? "bg-[#FF3B30]/15" : "bg-white/5"}`}>
                        {isGame ? <Trophy size={22} weight="fill" color="#FF3B30" /> : <Barbell size={22} weight="bold" color="#A3A3A3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-sm ${isGame ? "bg-[#FF3B30] text-white" : "bg-white/10 text-[#A3A3A3]"}`}>{e.type}</span>
                          <span className="font-semibold text-white truncate">{e.title}{e.opponent ? ` vs ${e.opponent}` : ""}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[#737373] mt-1 flex-wrap">
                          <span className="font-mono">{e.team_name}</span>
                          {e.location && <span className="flex items-center gap-1"><MapPin size={12} /> {e.location}</span>}
                          <span className="flex items-center gap-1"><Clock size={12} /> {fmtTime(e.start)}</span>
                        </div>
                      </div>
                      {canEdit && <button onClick={() => del(e.id)} className="text-[#737373] hover:text-[#FF3B30] p-2" data-testid={`delete-event-${e.id}`}><Trash size={18} weight="bold" /></button>}
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Add Event" testid="event-modal">
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Team</label>
            <select className={inputCls} value={form.team_id} onChange={(e) => setForm({ ...form, team_id: e.target.value })} data-testid="event-team-select">
              {teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Type</label>
            <div className="grid grid-cols-2 gap-2">
              {["practice", "game"].map((tp) => (
                <button key={tp} type="button" onClick={() => setForm({ ...form, type: tp })} data-testid={`event-type-${tp}`}
                  className={`py-3 rounded-sm text-xs uppercase tracking-widest font-semibold ${form.type === tp ? "bg-[#FF3B30] text-white" : "bg-[#0A0A0A] border border-white/15 text-[#A3A3A3]"}`}>{tp}</button>
              ))}
            </div>
          </div>
          <div><label className={labelCls}>Title</label><input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Team Practice" data-testid="event-title-input" /></div>
          {form.type === "game" && <div><label className={labelCls}>Opponent</label><input className={inputCls} value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} placeholder="Riverside High" data-testid="event-opponent-input" /></div>}
          <div><label className={labelCls}>Location</label><input className={inputCls} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Main Gym" data-testid="event-location-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className={labelCls}>Date</label><input type="date" className={inputCls} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="event-date-input" /></div>
            <div><label className={labelCls}>Time</label><input type="time" className={inputCls} value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} data-testid="event-time-input" /></div>
          </div>
          <button onClick={create} disabled={saving} className={`${btnPrimary} w-full`} data-testid="save-event-button">{saving ? "Saving…" : "Add Event"}</button>
        </div>
      </Modal>
    </div>
  );
}
